import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  NewsItem,
  NewsRecipient,
  NewsRecipientManager,
  NewsRecipientReading,
  NewsRecipientReadingManager,
  NewsRecipientReadingSchoolManagers,
  NewsRecipientSchoolManagers,
  NewsRecipientSchoolStudents,
  NewsRecipientSchoolTeachers,
  NewsRecipientStudent,
  NewsRecipientTeacher,
} from '../../../model/v3/news';
import { AdminNewsV3RestService } from '../../../services/rest/v3/admin-news-rest.service';
import { Page, Pageable } from '../../../model/rest/base';
import { SchoolV3RestService } from '../../../services/rest/v3/school-rest.service';
import { SchoolListView } from '../../../model/v3/schools';

@Component({
  selector: 'app-admin-news-details-page',
  templateUrl: './admin-news-details-page.component.html',
  styleUrls: ['./admin-news-details-page.component.scss'],
  standalone: false,
})
export class AdminNewsDetailsPageComponent implements OnInit {
  newsId: number | null = null;
  item: NewsItem | null = null;
  recipients: NewsRecipient[] = [];
  recipientsPage: Page<NewsRecipient> | null = null;
  recipientsPageable = new Pageable(0, 10, []);
  recipientsLoaded = false;
  recipientsReadingPage: Page<NewsRecipientReading> | null = null;
  recipientsReadingPageable = new Pageable(0, 10, []);
  recipientsReadingRows: RecipientReadingRow[] = [];
  schoolNamesById: Record<number, string> = {};

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private adminNewsRest: AdminNewsV3RestService,
    private schoolRest: SchoolV3RestService,
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('newsId');
    this.newsId = id ? +id : null;
    if (!this.newsId) {
      this.router.navigate(['/admin/news']);
      return;
    }
    this.adminNewsRest.getNewsById(this.newsId).subscribe((news) => {
      this.item = news;
    });
    this.loadRecipients();
    this.loadRecipientsReading();
  }

  edit(): void {
    if (!this.newsId) {
      return;
    }
    this.router.navigate(['/admin/news', this.newsId, 'edit']);
  }

  getLabels(): string[] {
    const labels = this.item?.content?.labels;
    if (!labels) {
      return [];
    }
    return labels.split(',').map((label) => label.trim()).filter((label) => !!label);
  }

  getRecipients(): NewsRecipient[] {
    return this.recipients;
  }

  isAllCurrentManagersRecipient(): boolean {
    return this.recipientsLoaded && this.recipients.length === 0;
  }

  getRecipientLabel(recipient: NewsRecipient): string {
    switch (recipient['@type']) {
      case 'NewsRecipientManager': {
        const manager = (recipient as NewsRecipientManager).manager;
        return `${manager?.person?.details?.name || ''} ${manager?.person?.details?.surname || ''}`.trim();
      }
      case 'NewsRecipientStudent': {
        const student = (recipient as NewsRecipientStudent).student;
        return `${student?.person?.details?.name || ''} ${student?.person?.details?.surname || ''}`.trim();
      }
      case 'NewsRecipientTeacher': {
        const teacher = (recipient as NewsRecipientTeacher).teacher;
        return `${teacher?.person?.details?.name || ''} ${teacher?.person?.details?.surname || ''}`.trim();
      }
      case 'NewsRecipientSchoolManagers': {
        const school = (recipient as NewsRecipientSchoolManagers).learningUnit;
        return `All managers in ${school?.details?.name || 'school'}`;
      }
      case 'NewsRecipientSchoolStudents': {
        const school = (recipient as NewsRecipientSchoolStudents).learningUnit;
        return `All students in ${school?.details?.name || 'school'}`;
      }
      case 'NewsRecipientSchoolTeachers': {
        const school = (recipient as NewsRecipientSchoolTeachers).learningUnit;
        return `All teachers in ${school?.details?.name || 'school'}`;
      }
      case 'NewsRecipientAllManagers':
        return 'All managers';
      case 'NewsRecipientAllStudents':
        return 'All students';
      case 'NewsRecipientAllTeachers':
        return 'All teachers';
      default:
        return recipient['@type'];
    }
  }

  getRecipientEmail(recipient: NewsRecipient): string | null {
    switch (recipient['@type']) {
      case 'NewsRecipientManager':
        return (recipient as NewsRecipientManager).manager?.person?.registrationEmail || null;
      case 'NewsRecipientStudent':
        return (recipient as NewsRecipientStudent).student?.person?.registrationEmail || null;
      case 'NewsRecipientTeacher':
        return (recipient as NewsRecipientTeacher).teacher?.person?.registrationEmail || null;
      default:
        return null;
    }
  }

  getRecipientTypeLabel(recipient: NewsRecipient): string {
    switch (recipient['@type']) {
      case 'NewsRecipientManager':
        return 'Manager';
      case 'NewsRecipientStudent':
        return 'Student';
      case 'NewsRecipientTeacher':
        return 'Teacher';
      case 'NewsRecipientSchoolManagers':
        return 'School managers';
      case 'NewsRecipientSchoolStudents':
        return 'School students';
      case 'NewsRecipientSchoolTeachers':
        return 'School teachers';
      case 'NewsRecipientAllManagers':
      case 'NewsRecipientAllStudents':
      case 'NewsRecipientAllTeachers':
        return 'Broadcast';
      default:
        return 'Recipient';
    }
  }

  switchRecipientsPage(pageable: Pageable): void {
    this.recipientsPageable = pageable;
    this.loadRecipients();
  }

  switchRecipientsReadingPage(pageable: Pageable): void {
    this.recipientsReadingPageable = pageable;
    this.loadRecipientsReading();
  }

  getRecipientsReadingRows(): RecipientReadingRow[] {
    return this.recipientsReadingRows;
  }

  private loadRecipients(): void {
    if (!this.newsId) {
      return;
    }
    this.adminNewsRest.listRecipients(this.newsId, this.recipientsPageable).subscribe((page) => {
      this.recipientsPage = page;
      this.recipients = page?.content ?? [];
      this.recipientsLoaded = true;
    });
  }

  private loadRecipientsReading(): void {
    if (!this.newsId) {
      return;
    }
    this.adminNewsRest.listRecipientsReadingDate(
      this.newsId,
      true,
      this.recipientsReadingPageable,
    ).subscribe((page) => {
      this.recipientsReadingPage = page;
      this.recipientsReadingRows = this.toRecipientReadingRows(page?.content ?? []);
      this.loadMissingSchoolNames(this.recipientsReadingRows);
    });
  }

  private toRecipientReadingRows(recipients: NewsRecipientReading[]): RecipientReadingRow[] {
    return recipients.flatMap((recipient) => {
      if (recipient['@type'] === 'NewsRecipientReadingManager') {
        const manager = (recipient as NewsRecipientReadingManager).manager;
        const readingDate = (recipient as NewsRecipientReadingManager).readingDate;
        return [this.toRow(manager, readingDate, null)];
      }
      if (recipient['@type'] === 'NewsRecipientReadingSchoolManagers') {
        const schoolRecipient = recipient as NewsRecipientReadingSchoolManagers;
        const schoolName = schoolRecipient.learningUnit?.details?.name || '-';
        const schoolId = schoolRecipient.learningUnit?.id || null;
        if (schoolId && schoolName && schoolName !== '-') {
          this.schoolNamesById[schoolId] = schoolName;
        }
        return (schoolRecipient.managers ?? []).map((managerRecipient) =>
          this.toRow(managerRecipient.manager, managerRecipient.readingDate, schoolName),
        );
      }
      return [];
    });
  }

  private toRow(
    manager: NewsRecipientReadingManager['manager'],
    readingDate: string | null,
    schoolNameFallback: string | null,
  ): RecipientReadingRow {
    const schoolId = manager?.school?.id || null;
    const schoolName = schoolId
      ? this.schoolNamesById[schoolId] || schoolNameFallback || `School #${schoolId}`
      : schoolNameFallback || '-';
    return {
      managerName: `${manager?.person?.details?.name || ''} ${manager?.person?.details?.surname || ''}`.trim() || '-',
      managerEmail: manager?.person?.registrationEmail || '-',
      schoolName,
      readingDate,
      schoolId,
    };
  }

  private loadMissingSchoolNames(rows: RecipientReadingRow[]): void {
    const missingSchoolIds = Array.from(new Set(
      rows
        .map((row) => row.schoolId)
        .filter((schoolId): schoolId is number => !!schoolId && !this.schoolNamesById[schoolId]),
    ));
    if (!missingSchoolIds.length) {
      return;
    }
    this.schoolRest.listSchoolsListView({
      ids: missingSchoolIds,
      pageable: new Pageable(0, missingSchoolIds.length, []),
    }).subscribe((page) => {
      (page?.content ?? []).forEach((school: SchoolListView) => {
        const schoolName = school?.details?.name || '-';
        if (school.id) {
          this.schoolNamesById[school.id] = schoolName;
        }
      });
      this.recipientsReadingRows = this.recipientsReadingRows.map((row) => {
        if (!row.schoolId) {
          return row;
        }
        return {
          ...row,
          schoolName: this.schoolNamesById[row.schoolId] || row.schoolName,
        };
      });
    });
  }
}

interface RecipientReadingRow {
  managerName: string;
  managerEmail: string;
  schoolName: string;
  readingDate: string | null;
  schoolId: number | null;
}
